import type { MessageDescriptor } from '@lingui/core';
import type { LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';
import { z } from 'zod';

import type { AnyScreenAction, Permission } from '@/shared/screen';

import { CATALOG_ID, descriptionIssues, paramsIssues, validateDataSource, type AnyDataSource, type DataShape, type SourceData } from './data-source';
import type { BlockSurface } from './host';

/** `screen` (só a tela usa) → `candidate` (portões verdes) → `catalog` (promoção humana, 09 §10.5). */
export type BlockStatus = 'screen' | 'candidate' | 'catalog';

/** Qual apresentação desenhar: `web` (P/M/G por container query) ou `mobile` (iPhone, < 768 px). */
export type BlockPresentation = 'web' | 'mobile';

export type SourceMap = Readonly<Record<string, AnyDataSource>>;

/** Dado de cada fonte do bloco, pelo nome dado em `sources`. */
export type BlockData<S extends SourceMap> = { readonly [K in keyof S]: SourceData<S[K]> };

/** Params de cada fonte, montados a partir dos params do bloco. */
export type BlockQuery<S extends SourceMap> = { readonly [K in keyof S]: z.input<S[K]['params']> };

/** Intenção de ação: o bloco pede, o hospedeiro executa (U41). Só ids declarados em `actions`. */
export type BlockRun = (actionId: string, input?: unknown) => void;

/** O que a apresentação recebe. Nada de URL, store ou registro em foco: só isto. */
export interface BlockViewProps<P, S extends SourceMap> {
  readonly params: P;
  readonly data: BlockData<S>;
  readonly run: BlockRun;
  /** Se quem vê pode rodar a ação; esconda o botão quando `false`. */
  readonly canRun: (actionId: string) => boolean;
  readonly surface: BlockSurface;
}

export interface BlockRender<P, S extends SourceMap> {
  readonly web: ComponentType<BlockViewProps<P, S>>;
  readonly mobile: ComponentType<BlockViewProps<P, S>>;
}

/** Forma do skeleton (U39 §8): a mesma do conteúdo final. */
export interface BlockSkeletonSpec {
  readonly kind: 'table' | 'list' | 'kpi' | 'record' | 'chart';
  readonly rows?: number;
  readonly columns?: number;
}

/** Vazio com próximo passo (U39 §9). A ação é um id de `actions`. */
export interface BlockEmptySpec {
  readonly title: MessageDescriptor;
  readonly description?: MessageDescriptor;
  readonly icon?: LucideIcon;
  readonly action?: { readonly id: string; readonly label: MessageDescriptor; readonly input?: unknown };
}

interface BlockCommon<PS extends z.ZodType, S extends SourceMap> {
  /** Estável, com o domínio na frente (`fund.list`); renomear é versão nova. */
  readonly id: string;
  /** Título da seção (pt-BR no Lingui). */
  readonly title: MessageDescriptor;
  /** Inglês canônico: é o que o modelo lê em `list_blocks` (U33). */
  readonly description: string;
  readonly status: BlockStatus;
  /** Única entrada do bloco: zod serializável (vira JSON Schema para o agente). */
  readonly params: PS;
  /** Fontes declaradas (≤ 6). O nome é a chave em `data`. */
  readonly sources: S;
  /** Params do bloco → params de cada fonte. Função pura. */
  readonly query: (params: z.output<PS>) => BlockQuery<S>;
  /** Ações de manifesto que o bloco pode pedir ao hospedeiro. */
  readonly actions?: readonly AnyScreenAction[];
  readonly empty?: BlockEmptySpec;
  /** Vazio sob medida; o padrão é "toda fonte vazia pela forma". */
  readonly isEmpty?: (data: BlockData<S>) => boolean;
  readonly skeleton?: { readonly web?: BlockSkeletonSpec; readonly mobile?: BlockSkeletonSpec };
}

/** Bloco de domínio: apresentação própria do módulo, web e iPhone (09 §10.2). */
export interface DomainBlockDefinition<PS extends z.ZodType, S extends SourceMap> extends BlockCommon<PS, S> {
  readonly render: BlockRender<z.output<PS>, S>;
}

/** Props que um componente genérico do catálogo recebe dentro de um bloco de configuração. */
export interface BlockComponentProps<C, D> {
  readonly props: C;
  readonly data: D;
  readonly run: BlockRun;
  readonly canRun: (actionId: string) => boolean;
  readonly surface: BlockSurface;
}

/** Componente genérico do catálogo (`DataView`, `KpiCard`…): props zod serializáveis + web e iPhone. */
export interface BlockComponent<C, D> {
  readonly id: string;
  readonly description: string;
  readonly props: z.ZodType<C>;
  readonly render: {
    readonly web: ComponentType<BlockComponentProps<C, D>>;
    readonly mobile: ComponentType<BlockComponentProps<C, D>>;
  };
}

/** Fontes cujo dado serve ao genérico `D`. */
export type SourceNamesFor<S extends SourceMap, D> = { [N in keyof S]: SourceData<S[N]> extends D ? N : never }[keyof S] & string;

/** Bloco de configuração: um genérico do catálogo + a configuração fixa do domínio (09 §3.2). */
export interface ConfigBlockDefinition<PS extends z.ZodType, S extends SourceMap, C, K extends keyof S & string> extends BlockCommon<PS, S> {
  /** Qual fonte alimenta o genérico. */
  readonly bind: K;
  /** Configuração do genérico; precisa passar no `component.props`. */
  readonly props: (params: z.output<PS>) => C;
}

export interface Block<PS extends z.ZodType = z.ZodType, S extends SourceMap = SourceMap> extends BlockCommon<PS, S> {
  readonly kind: 'domain' | 'config';
  readonly render: BlockRender<z.output<PS>, S>;
  /** Só em bloco de configuração: o que o `validatePage` e o catálogo enxergam. */
  readonly config?: {
    readonly component: string;
    readonly bind: string;
    readonly schema: z.ZodType;
    readonly props: (params: z.output<PS>) => unknown;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- registro mistura blocos de params e fontes diferentes
export type AnyBlock = Block<any, any>;

/** Declara um componente genérico do catálogo. Em dev, web e mobile iguais é erro (U41). */
export function defineBlockComponent<C, D>(component: BlockComponent<C, D>): BlockComponent<C, D> {
  if (import.meta.env.DEV) {
    const issues = [...descriptionIssues(`componente ${component.id}`, component.description)];
    if (component.render.web === component.render.mobile) issues.push(`componente ${component.id}: a apresentação mobile precisa ser dedicada (U41)`);
    if (issues.length > 0) throw new Error(issues.join('; '));
  }
  return component;
}

function configRender<PS extends z.ZodType, S extends SourceMap, C, D, K extends keyof S & string>(
  component: BlockComponent<C, D>,
  definition: ConfigBlockDefinition<PS, S, C, K>,
): BlockRender<z.output<PS>, S> {
  const { bind, props } = definition;
  const Web = component.render.web;
  const Mobile = component.render.mobile;
  const data = (view: BlockViewProps<z.output<PS>, S>) => view.data[bind] as D;
  const web = (view: BlockViewProps<z.output<PS>, S>) => (
    <Web props={props(view.params)} data={data(view)} run={view.run} canRun={view.canRun} surface={view.surface} />
  );
  const mobile = (view: BlockViewProps<z.output<PS>, S>) => (
    <Mobile props={props(view.params)} data={data(view)} run={view.run} canRun={view.canRun} surface={view.surface} />
  );
  return { web, mobile };
}

function checked<PS extends z.ZodType, S extends SourceMap>(block: Block<PS, S>): Block<PS, S> {
  if (import.meta.env.DEV) {
    const issues = validateBlock(block);
    if (issues.length > 0) throw new Error(`bloco ${block.id}: ${issues.join('; ')}`);
  }
  return block;
}

/** Declara um bloco de domínio (U41): apresentação própria, web e iPhone. Em dev, bloco inválido lança erro. */
export function defineBlock<PS extends z.ZodType, S extends SourceMap>(definition: DomainBlockDefinition<PS, S>): Block<PS, S> {
  return checked({ ...definition, kind: 'domain' });
}

/**
 * Declara um bloco de configuração: genérico do catálogo + props fixas do domínio (09 §3.2).
 * Em duas chamadas, `defineConfigBlock(KpiCard)({ … })`: o genérico fixa o tipo das props antes, e
 * os literais do `props` (enum, formato) não alargam para `string`.
 */
export function defineConfigBlock<C, D>(component: BlockComponent<C, D>) {
  return <PS extends z.ZodType, S extends SourceMap, K extends SourceNamesFor<S, D>>(definition: ConfigBlockDefinition<PS, S, C, K>): Block<PS, S> => {
    const { bind, props, ...common } = definition;
    return checked({
      ...common,
      kind: 'config',
      render: configRender(component, definition),
      config: { component: component.id, bind, schema: component.props, props },
    });
  };
}

const STATUSES: readonly BlockStatus[] = ['screen', 'candidate', 'catalog'];
export const MAX_BLOCK_SOURCES = 6;

export function validateBlock(block: AnyBlock): string[] {
  const owner = `bloco ${block.id}`;
  const issues: string[] = [];
  if (!CATALOG_ID.test(block.id)) issues.push(`${owner}: id inválido (use domínio.nome)`);
  issues.push(...descriptionIssues(owner, block.description));
  if (!STATUSES.includes(block.status)) issues.push(`${owner}: status inválido`);
  issues.push(...paramsIssues(owner, block.params as z.ZodType));

  const sources: readonly AnyDataSource[] = Object.values(block.sources as SourceMap);
  if (sources.length === 0) issues.push(`${owner}: declare ao menos uma fonte`);
  if (sources.length > MAX_BLOCK_SOURCES) issues.push(`${owner}: no máximo ${MAX_BLOCK_SOURCES} fontes (08 §4.4)`);
  for (const source of sources) issues.push(...validateDataSource(source));

  const seen = new Set<string>();
  for (const action of block.actions ?? []) {
    if (seen.has(action.id)) issues.push(`${owner}: ação duplicada ${action.id}`);
    seen.add(action.id);
    if (block.status !== 'screen' && action.page !== true) issues.push(`${owner}: ação ${action.id} sem page: true não entra no catálogo (08 §4.2)`);
  }
  if (block.empty?.action && !seen.has(block.empty.action.id)) issues.push(`${owner}: ação do vazio ${block.empty.action.id} não está em actions`);

  if (typeof block.render.web !== 'function' || typeof block.render.mobile !== 'function') {
    issues.push(`${owner}: apresentações web e mobile são obrigatórias (U41)`);
  } else if (block.kind === 'domain' && block.render.web === block.render.mobile) {
    issues.push(`${owner}: a apresentação mobile precisa ser dedicada (U41)`);
  }
  if (block.config) {
    try {
      z.toJSONSchema(block.config.schema);
    } catch {
      issues.push(`${owner}: props do componente ${block.config.component} precisam ser serializáveis (08 §4.3)`);
    }
  }
  return issues;
}

/** Permissões que o bloco exige: fontes (sem `'none'`) e ações, sem repetição. */
export function blockPermissions(block: AnyBlock): { readonly sources: readonly Permission[]; readonly actions: readonly Permission[]; readonly all: readonly Permission[] } {
  const unique = (list: readonly Permission[]) => {
    const seen = new Set<string>();
    return list.filter((p) => {
      const key = `${p.resource}:${p.action}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const sources = unique(Object.values(block.sources as SourceMap).flatMap((source) => (source.permission === 'none' ? [] : [source.permission])));
  const actions = unique((block.actions ?? []).flatMap((action) => (action.permission ? [action.permission] : [])));
  return { sources, actions, all: unique([...sources, ...actions]) };
}

/** Forma do skeleton padrão, pela forma da primeira fonte. */
export function defaultSkeleton(shape: DataShape | undefined, presentation: BlockPresentation): BlockSkeletonSpec {
  if (shape === 'scalar') return { kind: 'kpi' };
  if (shape === 'record') return { kind: 'record', rows: 4 };
  if (shape === 'series') return { kind: 'chart' };
  return presentation === 'web' ? { kind: 'table', rows: 5, columns: 4 } : { kind: 'list', rows: 4 };
}
